import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageBanner from "@/components/PageBanner";
import SectionTitle from "@/components/SectionTitle";
import FinalCTA from "@/components/FinalCTA";
import { IMAGES } from "@/lib/content";
import { Layers, MapPin, ShieldCheck, Handshake, Award } from "lucide-react";

const REASONS = [
  {
    title: "Integrated Service Model",
    text: "Five core services brought together under one brand.",
    icon: Layers,
  },
  {
    title: "Local Market Understanding",
    text: "Practical knowledge of Somali business and operating conditions.",
    icon: MapPin,
  },
  {
    title: "Reliable Client Support",
    text: "Service built around trust, communication, and accountability.",
    icon: ShieldCheck,
  },
  {
    title: "Flexible Engagement",
    text: "Suitable for both one time needs and longer term service arrangements.",
    icon: Handshake,
  },
  {
    title: "Professional Positioning",
    text: "A company image and service structure suitable for serious commercial and institutional clients.",
    icon: Award,
  },
];

const WhyAbayle = () => {
  return (
    <div className="min-h-screen flex flex-col bg-abayle-ivory">
      <Navbar />
      <main>
        <PageBanner
          eyebrow="Why Abayle"
          title="Why Abayle"
          subtitle="Built around reliability, local knowledge, responsive service, and integrated client support."
          image={IMAGES.logistics}
        />

        {/* Intro */}
        <section className="bg-white" data-testid="why-intro">
          <div className="container-abayle py-14 sm:py-20 lg:py-24 grid lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-2">
              <SectionTitle eyebrow="One Trusted Partner" title="Simplify Operations. Strengthen Execution." />
            </div>
            <div className="lg:col-span-3 text-abayle-charcoal/85 text-base lg:text-lg leading-relaxed">
              <p>
                Abayle Logistics and Trading is structured to make operations easier for clients who need multiple
                support services under one trusted company. Instead of dealing with separate providers for logistics,
                trade support, clearance, car sales, and car rental, clients can work through one accountable partner
                that understands the Somali market and values professional service.
              </p>
            </div>
          </div>
        </section>

        {/* Key reasons alternating */}
        <section className="bg-abayle-ivory" data-testid="why-reasons">
          <div className="container-abayle py-14 sm:py-20 lg:py-28">
            <SectionTitle centered eyebrow="Key Reasons" title="What Makes Abayle Different" />
            <div className="space-y-8">
              {REASONS.map((r, idx) => {
                const Icon = r.icon;
                const reverse = idx % 2 === 1;
                return (
                  <div
                    key={r.title}
                    className={`bg-white border border-abayle-gray-border rounded-lg p-6 lg:p-10 flex flex-col ${reverse ? "lg:flex-row-reverse" : "lg:flex-row"} gap-6 lg:gap-12 items-start lg:items-center shadow-sm hover:shadow-lg transition-all duration-300 group relative overflow-hidden`}
                    data-testid={`why-reason-${idx}`}
                  >
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-abayle-gold scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
                    <div className="shrink-0 flex items-center gap-4 sm:gap-5">
                      <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-md bg-abayle-green text-white flex items-center justify-center border-2 border-abayle-gold">
                        <Icon className="h-5 w-5 sm:h-7 sm:w-7" />
                      </div>
                      <span className="text-4xl sm:text-5xl lg:text-6xl font-bold text-abayle-gold/70">0{idx + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-abayle-green mb-2 sm:mb-3 tracking-tight">{r.title}</h3>
                      <p className="text-abayle-charcoal/80 text-sm sm:text-base lg:text-lg leading-relaxed">{r.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default WhyAbayle;
